import React from 'react';
import {envConfig, goBack, goTo, navigationRef, replace} from '@/utils';
import globalStore from '@/services/global.state';
import {homePage} from '@/config';
import {BasicObject} from '@/types';
import SportSpin from '@/components/basic/sportspin';
import Refresh from '@/components/basic/error-pages/refresh';
import DetailNavTitle from '@/components/business/detail-nav-title';
import Wallet from '@/common-pages/wallet';
import i18n from '@/i18n';
import {runAfterFirstInteraction, runWhenBrowserIdle} from '@/utils/defer';
import {getGR8Token} from '@/pages/home/home.service';
import {
  buildGr8Url,
  getGr8NavigationPath,
  normalizeGr8Path,
  requiresGr8Bootstrap,
  shouldHideSportsNavigation,
} from './gr8-web.utils';
import {getSportsPreloadStrategy} from './gr8-preload';
import {useSportsIframeStore} from './gr8.store';

const LOW_RESOURCE_KEEP_ALIVE_MS = 5 * 60 * 1000;
const GR8_LOGOUT_SETTLE_MS = 500;

let pendingTokenRequest:
  | {hostToken: string; promise: Promise<string>}
  | undefined;

function getGr8SessionToken(hostToken: string) {
  if (pendingTokenRequest?.hostToken === hostToken) {
    return pendingTokenRequest.promise;
  }

  const promise = getGR8Token()
    .then(result => {
      if (!result?.token) {
        throw new Error('GR8 token is empty');
      }
      return result.token;
    })
    .finally(() => {
      if (pendingTokenRequest?.promise === promise) {
        pendingTokenRequest = undefined;
      }
    });

  pendingTokenRequest = {hostToken, promise};
  return promise;
}

const SportsIframeHost = () => {
  const desiredPath = useSportsIframeStore(state => state.desiredPath);
  const requested = useSportsIframeStore(state => state.requested);
  const visible = useSportsIframeStore(state => state.visible);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const desiredPathRef = React.useRef(desiredPath);
  const pendingBootstrapPathRef = React.useRef<string>();
  const iframeReadyRef = React.useRef(false);
  const gr8TokenRef = React.useRef<string>();
  const languageRef = React.useRef(globalStore.lang);
  const hostTokenRef = React.useRef<string>();
  const authResolvedRef = React.useRef(false);
  const authTransitionRef = React.useRef(0);
  const wasVisibleRef = React.useRef(visible);
  const [iframeUrl, setIframeUrl] = React.useState('');
  const [iframeSessionVersion, setIframeSessionVersion] = React.useState(0);
  const [pageLoading, setPageLoading] = React.useState(true);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [showTitle, setShowTitle] = React.useState(
    () => !shouldHideSportsNavigation(desiredPath),
  );
  const [retryVersion, setRetryVersion] = React.useState(0);
  const strategyRef = React.useRef(getSportsPreloadStrategy(navigator));

  const resetIframe = React.useCallback(() => {
    authResolvedRef.current = false;
    iframeReadyRef.current = false;
    gr8TokenRef.current = undefined;
    hostTokenRef.current = undefined;
    authTransitionRef.current += 1;
    pendingBootstrapPathRef.current = undefined;
    setIframeUrl('');
    setLoadFailed(false);
    setPageLoading(true);
  }, []);

  const loadPath = React.useCallback((path?: string, forceReload = false) => {
    const normalizedPath = normalizeGr8Path(path);
    desiredPathRef.current = normalizedPath;
    setShowTitle(!shouldHideSportsNavigation(normalizedPath));

    if (!authResolvedRef.current || !envConfig.gr8IframeUrl) {
      return;
    }

    if (
      !forceReload &&
      iframeReadyRef.current &&
      iframeRef.current?.contentWindow
    ) {
      pendingBootstrapPathRef.current = undefined;
      iframeRef.current.contentWindow.postMessage(
        {type: 'iframe-navigation', value: {path: normalizedPath}},
        new URL(envConfig.gr8IframeUrl).origin,
      );
      return;
    }

    const pathToLoad =
      !iframeReadyRef.current && requiresGr8Bootstrap(normalizedPath)
        ? '/'
        : normalizedPath;
    pendingBootstrapPathRef.current =
      pathToLoad === normalizedPath ? undefined : normalizedPath;

    const nextUrl = buildGr8Url({
      baseUrl: envConfig.gr8IframeUrl,
      language: languageRef.current,
      path: pathToLoad,
      token: gr8TokenRef.current,
    });
    setIframeUrl(currentUrl => {
      if (currentUrl === nextUrl) {
        return currentUrl;
      }
      setLoadFailed(false);
      setPageLoading(true);
      return nextUrl;
    });
  }, []);

  React.useEffect(() => {
    const preload = () => useSportsIframeStore.getState().preload();
    if (strategyRef.current === 'idle') {
      runWhenBrowserIdle(preload, 3000);
      return;
    }
    if (strategyRef.current === 'interaction') {
      return runAfterFirstInteraction(preload);
    }
  }, []);

  React.useEffect(() => {
    if (!requested) {
      resetIframe();
      return;
    }

    let active = true;
    globalStore.asyncGetItem('token').then(storedToken => {
      if (!active || authResolvedRef.current) {
        return;
      }
      const initialToken = globalStore.token || storedToken || '';
      hostTokenRef.current = initialToken;
      const prepareToken = initialToken
        ? getGr8SessionToken(initialToken)
        : Promise.resolve(undefined);
      prepareToken
        .then(gr8Token => {
          if (!active) {
            return;
          }
          gr8TokenRef.current = gr8Token;
          authResolvedRef.current = true;
          loadPath(desiredPathRef.current);
        })
        .catch(() => {
          if (!active) {
            return;
          }
          setPageLoading(false);
          setLoadFailed(true);
        });
    });

    return () => {
      active = false;
    };
  }, [loadPath, requested, resetIframe, retryVersion]);

  React.useEffect(() => {
    if (!requested) {
      return;
    }

    let active = true;
    const subscription = globalStore.tokenSubject.subscribe(token => {
      if (!authResolvedRef.current) {
        return;
      }
      const nextHostToken = token || '';
      if (nextHostToken === hostTokenRef.current) {
        return;
      }

      const previousHostToken = hostTokenRef.current;
      hostTokenRef.current = nextHostToken;
      const transitionVersion = ++authTransitionRef.current;
      const shouldClearPreviousSession = Boolean(previousHostToken);
      if (shouldClearPreviousSession) {
        iframeRef.current?.contentWindow?.postMessage(
          {type: 'iframe', value: {logout: true}},
          new URL(envConfig.gr8IframeUrl).origin,
        );
      }

      setPageLoading(true);
      iframeReadyRef.current = false;
      gr8TokenRef.current = undefined;
      const prepareToken = nextHostToken
        ? getGr8SessionToken(nextHostToken)
        : Promise.resolve(undefined);
      const waitForPreviousSession = shouldClearPreviousSession
        ? new Promise<void>(resolve => {
            setTimeout(resolve, GR8_LOGOUT_SETTLE_MS);
          })
        : Promise.resolve();
      Promise.all([prepareToken, waitForPreviousSession])
        .then(([gr8Token]) => {
          if (!active || transitionVersion !== authTransitionRef.current) {
            return;
          }
          gr8TokenRef.current = gr8Token;
          // The guest iframe can report navigation while the new GR8 token is
          // being fetched and mark itself ready again. Always reload here so
          // the authenticated JWT reaches GR8 instead of only navigating the
          // existing guest session.
          iframeReadyRef.current = false;
          pendingBootstrapPathRef.current = undefined;
          setIframeSessionVersion(version => version + 1);
          loadPath(desiredPathRef.current, true);
        })
        .catch(() => {
          if (!active || transitionVersion !== authTransitionRef.current) {
            return;
          }
          setPageLoading(false);
          setLoadFailed(true);
        });
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [loadPath, requested]);

  React.useEffect(() => {
    if (!requested) {
      return;
    }
    const subscription = globalStore.langSubject.subscribe(language => {
      if (languageRef.current === language) {
        return;
      }
      languageRef.current = language;
      if (!authResolvedRef.current) {
        return;
      }
      iframeReadyRef.current = false;
      loadPath(desiredPathRef.current);
    });
    return () => subscription.unsubscribe();
  }, [loadPath, requested]);

  React.useEffect(() => {
    desiredPathRef.current = desiredPath;
    if (requested) {
      loadPath(desiredPath);
    }
  }, [desiredPath, loadPath, requested]);

  React.useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = visible;
    if (
      !wasVisible ||
      visible ||
      !requested ||
      !authResolvedRef.current ||
      !iframeReadyRef.current ||
      !iframeRef.current?.contentWindow ||
      !envConfig.gr8IframeUrl
    ) {
      return;
    }

    pendingBootstrapPathRef.current = undefined;
    desiredPathRef.current = '/';
    iframeRef.current.contentWindow.postMessage(
      {type: 'iframe-navigation', value: {path: '/'}},
      new URL(envConfig.gr8IframeUrl).origin,
    );
  }, [requested, visible]);

  React.useEffect(() => {
    if (visible || !requested || strategyRef.current !== 'click') {
      return;
    }
    const timer = setTimeout(() => {
      useSportsIframeStore.getState().destroy();
    }, LOW_RESOURCE_KEEP_ALIVE_MS);
    return () => clearTimeout(timer);
  }, [requested, visible]);

  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      if (!envConfig.gr8IframeUrl) {
        return;
      }
      try {
        if (event.origin !== new URL(envConfig.gr8IframeUrl).origin) {
          return;
        }
      } catch {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const data = event.data as string | BasicObject;
      if (!data || typeof data !== 'object') {
        return;
      }
      if (data.type === 'login.click') {
        goTo('Login', {backPage: homePage, sucessPage: homePage});
        return;
      }
      if (data.type === 'auth.fail') {
        if (!globalStore.token) {
          return;
        }
        goTo('Login', {backPage: homePage, sucessPage: homePage});
        return;
      }
      if (data.type === 'user.balance') {
        globalStore.updateAmount.next({});
        return;
      }
      if (data.type !== 'navigation') {
        return;
      }

      const pendingPath = pendingBootstrapPathRef.current;
      if (pendingPath) {
        iframeReadyRef.current = true;
        pendingBootstrapPathRef.current = undefined;
        loadPath(pendingPath);
        return;
      }

      const navigationPath = getGr8NavigationPath(data, languageRef.current);
      desiredPathRef.current = navigationPath;
      setShowTitle(!shouldHideSportsNavigation(navigationPath));
      setPageLoading(false);
      if (useSportsIframeStore.getState().visible) {
        window.history.replaceState(
          null,
          '',
          `${window.location.origin}/sports?path=${encodeURIComponent(
            navigationPath,
          )}`,
        );
      }
    },
    [loadPath],
  );

  React.useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  if (!requested) {
    return null;
  }

  return (
    <div
      aria-hidden={!visible}
      className={`absolute inset-0 flex flex-col overflow-hidden bg-[#f5f5f5] transition-opacity ${
        visible
          ? 'pointer-events-auto z-[100] opacity-100'
          : 'pointer-events-none -z-10 invisible opacity-0'
      }`}>
      {showTitle && (
        <DetailNavTitle
          onBack={() => {
            useSportsIframeStore.getState().hide();
            if (navigationRef.isReady() && navigationRef.canGoBack()) {
              goBack();
              return;
            }
            replace(homePage, {screen: 'Home'});
          }}
          title={i18n.t('home.tab.sports')}
          hideAmount
          hideServer
          themeType="dark"
          rightNode={<Wallet theme="dark" />}
        />
      )}
      {loadFailed ? (
        <Refresh
          onTryAgain={() => {
            resetIframe();
            setRetryVersion(version => version + 1);
          }}
        />
      ) : (
        <SportSpin loading={pageLoading}>
          {iframeUrl ? (
            <iframe
              key={iframeSessionVersion}
              ref={iframeRef}
              title="Sports"
              src={iframeUrl}
              className="flex min-h-0 flex-1 border-0"
              height="100%"
              width="100%"
              onLoad={() => {
                if (pendingBootstrapPathRef.current) {
                  return;
                }
                iframeReadyRef.current = true;
                setPageLoading(false);
              }}
              onError={() => {
                setPageLoading(false);
                setLoadFailed(true);
              }}
              allow="accelerometer; autoplay; clipboard-read; clipboard-write; camera; microphone; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
            />
          ) : null}
        </SportSpin>
      )}
    </div>
  );
};

export default SportsIframeHost;
